import { useState } from "react";
import { FormattedMessage, FormattedNumber } from "react-intl";
import { STAGES } from "../../config.js";
import { Icon } from "../../components/Icon.jsx";
import { Spinner, StatusPill } from "../../components/ui.jsx";

const stageKey = (stage) => (typeof stage === "number" ? STAGES[stage] : stage);

function StageIcon({ status }) {
  if (status === "running") return <Spinner size={14} />;
  if (status === "done") return <Icon name="check" size={14} strokeWidth={2.6} />;
  if (status === "failed") return <Icon name="x" size={14} strokeWidth={2.6} />;
  if (status === "skipped") return <Icon name="chevronRight" size={14} />;
  return <span className="stage-dot-inner" />;
}

/** The 7 pipeline stages (policy … correlate) with per-stage tool runs. */
export function StagePipeline({ job, collapsed, onToggle }) {
  const stages =
    job.progress?.stages?.length > 0
      ? job.progress.stages
      : STAGES.map((name, n) => ({ n, name, status: "pending" }));
  const running = stages.find((s) => s.status === "running");
  const [openStage, setOpenStage] = useState(null);
  const shown = openStage ?? running?.name ?? null;
  const runs = (job.toolRuns || []).filter((r) => stageKey(r.stage) === shown);
  const runCount = (name) => (job.toolRuns || []).filter((r) => stageKey(r.stage) === name).length;
  const doneCount = stages.filter((s) => ["done", "skipped"].includes(s.status)).length;

  return (
    <section className={`pipeline card ${collapsed ? "is-collapsed" : ""}`}>
      <div className="pipeline-head">
        <h2 className="h4">
          <FormattedMessage id="job.pipeline" />
        </h2>
        <span className="muted small">
          <FormattedMessage id="job.stagesDone" values={{ done: doneCount, total: stages.length }} />
        </span>
        <div className="progress-track" aria-hidden="true">
          <span className="progress-fill" style={{ inlineSize: `${(doneCount / stages.length) * 100}%` }} />
        </div>
        {onToggle ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onToggle} aria-expanded={!collapsed}>
            <FormattedMessage id={collapsed ? "job.showPipeline" : "job.hidePipeline"} />
            <Icon name="chevronDown" size={14} className={collapsed ? "" : "flip-v"} />
          </button>
        ) : null}
      </div>

      {!collapsed ? (
        <>
          <ol className="stages">
            {stages.map((s) => (
              <li key={s.name} className={`stage stage-${s.status} ${shown === s.name ? "is-open" : ""}`}>
                <button type="button" className="stage-btn" onClick={() => setOpenStage(s.name)}>
                  <span className="stage-dot">
                    <StageIcon status={s.status} />
                  </span>
                  <span className="stage-name">
                    <FormattedMessage id={`stage.${s.name}`} defaultMessage={s.name} />
                  </span>
                  <span className="stage-status small">
                    <FormattedMessage id={`stageStatus.${s.status}`} defaultMessage={s.status} />
                    {runCount(s.name) ? (
                      <span className="muted">
                        {" · "}
                        <FormattedMessage id="job.toolRuns" values={{ count: runCount(s.name) }} />
                      </span>
                    ) : null}
                  </span>
                </button>
              </li>
            ))}
          </ol>

          {shown ? (
            <div className="tool-runs">
              <div className="tool-runs-head small muted">
                <FormattedMessage
                  id="job.toolRunsFor"
                  values={{ stage: <FormattedMessage id={`stage.${shown}`} defaultMessage={shown} /> }}
                />
              </div>
              {runs.length === 0 ? (
                <p className="muted small">
                  <FormattedMessage id="job.noToolRuns" />
                </p>
              ) : (
                <ul className="run-list">
                  {runs.map((r) => (
                    <li key={r.id} className="run-item">
                      <span className="mono truncate">{r.sourceId}</span>
                      <StatusPill ns="runStatus" value={r.status} />
                      <span className="muted small mono end-text">
                        {r.durationMs != null ? (
                          <FormattedNumber value={r.durationMs / 1000} style="unit" unit="second" maximumFractionDigits={1} />
                        ) : (
                          "—"
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
